import React, { useState, useEffect, useRef } from 'react';
import { sellerDashboardAPI, sellerProfileAPI } from '../../services/api.service';
import { downloadSubscriptionInvoice } from '../../utils/subscriptionInvoice';
import '../styles/MyPlansPage.css';

const MyPlansPage = () => {
  const topRef = useRef(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [billingProfile, setBillingProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [subscriptionResponse, profileResponse] = await Promise.allSettled([
        sellerDashboardAPI.getSubscriptions(),
        sellerProfileAPI.get(),
      ]);

      if (subscriptionResponse.status === 'fulfilled') {
        const response = subscriptionResponse.value;
        if (response.success) {
          setSubscriptionData(response.data);
        } else {
          setError(response.message || 'Failed to load subscription data');
        }
      } else {
        throw subscriptionResponse.reason;
      }

      if (profileResponse.status === 'fulfilled' && profileResponse.value?.success) {
        setBillingProfile(profileResponse.value.data?.profile || {});
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      setError('Failed to load subscription data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)
      .toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
      .replace(/\b(am|pm)\b/g, (match) => match.toUpperCase());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getUserProfile = () => {
    try {
      return JSON.parse(localStorage.getItem('userData') || 'null') || {};
    } catch (err) {
      return {};
    }
  };

  const handleDownloadInvoice = (subscription) => {
    const localUserProfile = getUserProfile();
    const resolvedProfile = {
      ...localUserProfile,
      ...billingProfile,
      gst_number: subscription.gst_number || billingProfile.gst_number,
      user_type: localUserProfile.user_type || billingProfile.user_type,
    };
    downloadSubscriptionInvoice(subscription, resolvedProfile);
  };

  const getStatusBadge = (status, daysRemaining) => {
    if (status !== 'active') {
      return <span className="mp-status-badge-myplans inactive">{status}</span>;
    }

    if (daysRemaining === null) {
      return <span className="mp-status-badge-myplans active">Active</span>;
    }

    if (daysRemaining === 0) {
      return <span className="mp-status-badge-myplans expired">Expired</span>;
    }

    if (daysRemaining <= 7) {
      return <span className="mp-status-badge-myplans urgent">Active ({daysRemaining + 1} days left)</span>;
    }

    return <span className="mp-status-badge-myplans active">Active ({daysRemaining + 1} days left)</span>;
  };

  if (loading) {
    return (
      <div className="mp-page-myplans">
        <div className="mp-container-myplans">
          <div className="loading-spinner-myplans">
            <div className="spinner-myplans"></div>
            <p>Loading your plans...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mp-page-myplans">
        <div className="mp-container-myplans">
          <div className="mp-error-message-myplans">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3>Error Loading Plans</h3>
            <p>{error}</p>
            <button onClick={fetchSubscriptionData} className="mp-retry-btn-myplans">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionData || subscriptionData.subscriptions.length === 0) {
    return (
      <div className="mp-page-myplans">
        <div className="mp-container-myplans">
          <header className="mp-header-myplans">
            <h1>My Plans & Payment History</h1>
            <p>View your subscription plans and payment history</p>
          </header>

          <div className="mp-empty-state-myplans">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <h3>No Plans Found</h3>
            <p>You haven't purchased any plans yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mp-page-myplans" ref={topRef}>
      <div className="mp-container-myplans">
        <header className="mp-header-myplans">
          <h1>My Plans & Payment History</h1>
          <p>View your subscription plans and payment history</p>
        </header>

        <div className="mp-summary-cards-myplans">
          <div className="mp-summary-card-myplans">
            <div className="mp-summary-icon-myplans">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="mp-summary-content-myplans">
              <h3>{subscriptionData.summary.total_plans}</h3>
              <p>Total Plans</p>
            </div>
          </div>

          <div className="mp-summary-card-myplans">
            <div className="mp-summary-icon-myplans">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M9 9h6v6H9z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            <div className="mp-summary-content-myplans">
              <h3>{subscriptionData.summary.active_plans}</h3>
              <p>Active Plans</p>
            </div>
          </div>

          <div className="mp-summary-card-myplans">
            <div className="mp-summary-icon-myplans">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M7 5H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M7 9H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 5C9 8.2 10.9 9.8 14 9.8H7L15.5 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="mp-summary-content-myplans">
              <h3>{formatCurrency(subscriptionData.summary.total_spent_rupees)}</h3>
              <p>Total Spent</p>
            </div>
          </div>
        </div>

        <div className="mp-plans-list-myplans">
          <h2>Your Subscription History</h2>
          <div className="mp-table-container-myplans">
            <table className="mp-table-myplans">
              <thead>
                <tr>
                  <th>Plan Details</th>
                  <th>Payment Info</th>
                  <th>Status</th>
                  <th>Validity</th>
                  <th>Payment Date</th>
                  <th>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {subscriptionData.subscriptions.map((subscription) => (
                  <tr key={subscription.id}>
                    <td className="mp-plan-details-myplans">
                      <div className="mp-plan-name-myplans">{subscription.plan_name}</div>
                      <div className="mp-plan-meta-myplans">
                        {subscription.properties_limit} property | {subscription.duration_months} months
                      </div>
                    </td>
                    <td className="mp-payment-info-myplans">
                      <div className="mp-payment-amount-myplans">{formatCurrency(subscription.amount_rupees)}</div>
                      <div className="mp-payment-breakdown-myplans">
                        Base: {formatCurrency(subscription.base_rupees)}
                        <br />
                        {subscription.tax_type === 'cgst_sgst'
                          ? `CGST: ${formatCurrency(subscription.cgst_rupees)} | SGST: ${formatCurrency(subscription.sgst_rupees)}`
                          : `IGST: ${formatCurrency(subscription.igst_rupees || subscription.gst_rupees)}`}
                      </div>
                      <div className="mp-payment-tax-type-myplans">Place of Supply: {subscription.place_of_supply || 'N/A'}</div>
                      <div className="mp-payment-id-myplans">ID: {subscription.razorpay_payment_id}</div>
                    </td>
                    <td className="mp-status-cell-myplans">{getStatusBadge(subscription.status, subscription.days_remaining)}</td>
                    <td className="mp-validity-cell-myplans">
                      <div className="mp-validity-dates-myplans">
                        <div>From: {formatDate(subscription.starts_at)}</div>
                        <div>To: {subscription.ends_at ? formatDate(subscription.ends_at) : 'Ongoing'}</div>
                      </div>
                    </td>
                    <td className="mp-payment-date-myplans">{formatDate(subscription.created_at)}</td>
                    <td className="mp-invoice-cell-myplans">
                      <button type="button" className="mp-invoice-btn-myplans" onClick={() => handleDownloadInvoice(subscription)}>
                        Download Invoice
                      </button>
                    </td>
                  </tr>
                ))}
                  
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPlansPage;
